import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import { SendHistoryList, TableHeader, ColumnMenu } from "@/shared/type";
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    filterDefs: TableFilterDefs;                                               // フィルタ定義
    filters: TableFilters;                                                     // フィルタ情報
    sendHistoryListTableHeaders: TableHeader[];                                // 送付履歴一覧ヘッダ情報
    sendHistoryListItem: SendHistoryList[];                                    // 送付履歴一覧
    totalCount: number;                                                        // 送付履歴一覧総件数
    pageOptions: DataOptions;                                                  // ページ情報を保存する変数
    isLoading: boolean;                                                        // テーブルがロード中: true ロード中でない: false
    sendInfoDeleteDialog: ColumnMenu;                                          // 送付履歴削除ダイアログ
    visibleRowMenu: ColumnMenu;                                                // 行メニュー表示切替
    isDeleteLoading: boolean;                                                  // 送付履歴削除のローダー
    isFromDetail: boolean;                                                     // 詳細画面からの遷移フラグ
}

/**
 * 重要情報のお知らせ：送付履歴一覧画面コンポーネント
 */
export default defineComponent({
    components: { CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                              // ルータ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                  // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                     // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                     // 1ページあたりの行数（状態管理用、デフォルト値：50）
        const sortBy = ref<string[]>([]);                                      // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                   // ソート順（状態管理用）
        let isPageLastData = false;                                            // ページの最終データフラグ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;               // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                      // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const filterRef = ref<any>(null);                                      // フィルタコンポーネント

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // ウィンドウサイズ
            filterDefs: {                                                      // フィルタ定義
                'freeWord': { title: 'フリーワード', types: 'FreeWord' },
                'sendDate': { title: '送付日', types: 'DatePeriod' }
            },
            filters: {},                                                       // フィルタ情報
            sendHistoryListTableHeaders: [],                                   // 送付履歴一覧ヘッダー
            sendHistoryListItem: [],                                           // 送付履歴一覧
            totalCount: 0,                                                     // 送付履歴一覧総件数
            pageOptions: {                                                     // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                  // テーブルがロード中: true ロード中でない: false
            sendInfoDeleteDialog: {},                                          // 送付履歴削除ダイアログ
            visibleRowMenu: {},                                                // 行メニュー表示切替
            isDeleteLoading: false,                                            // 送付履歴削除のローダー
            isFromDetail: false                                                // 詳細画面からの遷移フラグ
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];         // 送付履歴一覧のヘッダ色
        const tableHeight = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_SEND_HISTORY_LIST;
        // 送付履歴一覧ヘッダ情報
        const sendHistoryListDisplayTableHeaders: TableHeader[] = [
            {
                text: 'タイトル',
                value: 'title',
                width: '80%',
                class: tableHeaderClass
            },
            {
                text: '送付日',
                value: 'sendDate',
                width: '15%',
                class: tableHeaderClass
            },
            {
                text: '',
                value: 'icons',
                width: '5%',
                sortable: false,
                align: 'end',
                class: tableHeaderClass
            }
        ];

        // フィルタバリデーション
        // フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '送付履歴一覧');

            // storeからフィルター条件をすべて復元
            state.filters = ctx.root.$store.state.importantInformation.filtersSendHistory;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.importantInformation.pageNationSendHistory;
            itemsPerPage.value = ctx.root.$store.state.importantInformation.itemsPerPageSendHistory;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.importantInformation.sortBySendHistory;
            sortDesc.value = ctx.root.$store.state.importantInformation.sortDescSendHistory;

            // storeから詳細画面からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.importantInformation.isFromDetailSendHistory;

            // フィルタ情報を初期設定
            // awaitで処理の完了を待つ
            await initFilter();

            // 初期表示ヘッダ設定
            state.sendHistoryListTableHeaders = sendHistoryListDisplayTableHeaders;

            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filtersのプロパティにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // なければ送付履歴一覧情報を設定
                updateList();
            }

            // 詳細画面からの遷移フラグにfalseを設定
            // trueのままだと通常のフィルタリング時に1ページ目へ戻らなくなるため
            state.isFromDetail = false;
        }

        /**
         * フィルタバリデーションを設定
         * awaitを行うための関数
         */
        function initFilter() {
            //   以下の項目にフィルタバリデーションを設定
            //   フリーワード
            state.filterDefs['freeWord'].validator = freeWordRules;
        }

        /**
         * 送付履歴一覧の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseにリセット
            isPageLastData = false;

            // 現在のページ情報/ソート情報、フィルタ条件をストアに保存
            ctx.root.$store.commit('importantInformation/setPageNationSendHistory', pageNation.value);
            ctx.root.$store.commit('importantInformation/setItemsPerPageSendHistory', itemsPerPage.value);
            ctx.root.$store.commit('importantInformation/setFiltersSendHistory', state.filters);
            ctx.root.$store.commit('importantInformation/setSortBySendHistory', sortBy.value);
            ctx.root.$store.commit('importantInformation/setSortDescSendHistory', sortDesc.value);

            // makeListParametersでAPIパラメータを作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 重要情報のお知らせ送付履歴一覧取得APIでデータを取得する
            // GET /api/important-information/send-histories
            // パラメータ：ページ番号
            //            1ページあたりの行数
            //            ソートキー
            //            ソート順
            //            フィルタ条件
            const response = await ctx.root.$store.dispatch('importantInformation/getSendHistoryInformation',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 送付履歴一覧のデータをstate.sendHistoryListItemへ設定する
            state.sendHistoryListItem = response.data.sendHistoryLists;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.sendHistoryListItem.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 送付履歴一覧を更新する
            updateList();
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filters = filters;

            // 詳細画面からの遷移でない場合
            if (!state.isFromDetail) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 送付履歴一覧を更新する
            updateList();
        }

        /**
         * 重要情報のお知らせの送付履歴データを削除する
         * 
         * @param importantId 重要情報ID
         */
        async function sendHistoryDelete(importantId: string) {
            // 削除中のローダーをtrueにする
            state.isDeleteLoading = true;

            // 送付履歴削除APIを呼ぶ
            //   DELETE /api/important-information/send-history/{importantId}
            //   パラメータ：重要情報ID
            const response = await ctx.root.$store.dispatch('importantInformation/deleteSendHistoryInformation',
                {
                    importantId
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 送付履歴削除ダイアログを閉じる
                state.sendInfoDeleteDialog[importantId] = false;

                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 送付履歴削除ダイアログを閉じる
            state.sendInfoDeleteDialog[importantId] = false;

            // 削除完了のダイアログを表示する
            commonRef.value.displayMessageDialog(response);

            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                state.pageOptions.page -= 1;
            }

            // 送付履歴一覧を更新する
            updateList();
        }

        /**
         * 送付履歴詳細へ遷移する
         */
        function navigationHistory(infoId: string) {
            // storeの詳細画面からの遷移フラグをtrueに設定
            ctx.root.$store.commit('importantInformation/setIsFromDetailSendHistory', true);

            router.push('/important-information/send-history/detail/' + infoId);
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            tableHeight,
            pageNation,
            itemsPerPage,
            linesPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            commonRef,
            filterRef,
            updateOptions,
            updateFilters,
            sendHistoryDelete,
            navigationHistory,
            onResize
        };
    }
});
